/**
 * Continuous sounds (GDD §9): the jet engine (pitch and volume follow the throttle, afterburner roar on boost),
 * positional helicopter rotors, gunboat engines and enemy jets with Doppler, the refuel tone and the low-fuel alarm.
 * All are persistent voices driven by scheduled automation (judgment call 42).
 */
import { alarmPeriod, createEngineTone, engineTone, hz, REFUEL_PERIOD, refuelHz, type EngineTone } from './tuning.ts';
import { glide, modulate, noiseSource, note, panner, setAt, silentGain } from './voices.ts';

function bandpass(ctx: BaseAudioContext, type: BiquadFilterType, f: number, q: number): BiquadFilterNode {
  const b = ctx.createBiquadFilter();
  b.type = type;
  b.frequency.value = f;
  b.Q.value = q;
  return b;
}

function osc(ctx: BaseAudioContext, type: OscillatorType, f: number): OscillatorNode {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.value = f;
  o.start();
  return o;
}

// ---------------------------------------------------------------------------------------------------------------
// The jet engine

/** The engine's level in the mix: present under everything, never in the way. */
const ENGINE_LEVEL = 0.5;

export interface EngineVoice {
  readonly out: GainNode;
  readonly saw1: OscillatorNode;
  readonly saw2: OscillatorNode;
  readonly body: BiquadFilterNode;
  readonly rumble: GainNode;
  readonly whine: OscillatorNode;
  readonly whineGain: GainNode;
  readonly air: BiquadFilterNode;
  readonly airGain: GainNode;
  readonly burner: GainNode;
  readonly tone: EngineTone;
  /** Last speed and life sent, so parameters only move when something changed. */
  speed: number;
  alive: boolean;
}

export function createEngineVoice(ctx: BaseAudioContext, noise: AudioBuffer, dest: AudioNode): EngineVoice {
  const out = silentGain(ctx, dest, 0);
  // Turbine body: two saws a few cents apart through a low-pass.
  const body = bandpass(ctx, 'lowpass', 600, 1.2);
  const rumble = silentGain(ctx, out);
  body.connect(rumble);
  const saw1 = osc(ctx, 'sawtooth', 58);
  const saw2 = osc(ctx, 'sawtooth', 58);
  saw2.detune.value = 11;
  saw1.connect(body);
  saw2.connect(body);
  // Compressor whine.
  const whine = osc(ctx, 'sine', 1000);
  const whineGain = silentGain(ctx, out);
  whine.connect(whineGain);
  // Air: band-passed noise.
  const src = noiseSource(ctx, noise, 0.37);
  const air = bandpass(ctx, 'bandpass', 1000, 0.9);
  const airGain = silentGain(ctx, out);
  src.connect(air).connect(airGain);
  // Afterburner: a low roar of noise.
  const burnLow = bandpass(ctx, 'lowpass', 520, 0.6);
  const burner = silentGain(ctx, out);
  src.connect(burnLow).connect(burner);
  return {
    out,
    saw1,
    saw2,
    body,
    rumble,
    whine,
    whineGain,
    air,
    airGain,
    burner,
    tone: createEngineTone(),
    speed: -1,
    alive: false,
  };
}

/** The engine at a speed; a lost jet's engine cuts out, and spools back up after the respawn. */
export function setEngine(v: EngineVoice, now: number, speed: number, alive: boolean): void {
  if (alive !== v.alive) {
    v.alive = alive;
    glide(v.out.gain, now, alive ? ENGINE_LEVEL : 0, alive ? 0.25 : 0.08);
  }
  if (Math.abs(speed - v.speed) < 0.05) return;
  v.speed = speed;
  const t = engineTone(speed, v.tone);
  const tc = 0.06;
  glide(v.saw1.frequency, now, t.rumbleHz, tc);
  glide(v.saw2.frequency, now, t.rumbleHz, tc);
  glide(v.body.frequency, now, t.cutoffHz, tc);
  glide(v.rumble.gain, now, t.rumbleGain, tc);
  glide(v.whine.frequency, now, t.whineHz, tc);
  glide(v.whineGain.gain, now, t.whineGain, tc);
  glide(v.air.frequency, now, t.airHz, tc);
  glide(v.airGain.gain, now, t.airGain, tc);
  glide(v.burner.gain, now, t.burner * 0.55, 0.12);
}

// ---------------------------------------------------------------------------------------------------------------
// Enemies

export type LoopKind = 'heli' | 'ship' | 'jet';

export interface LoopVoice {
  readonly kind: LoopKind;
  readonly level: GainNode;
  readonly pan: PannerNode;
  /** Enemy jets: the noise source's rate, the whine and the band-pass all follow Doppler. */
  readonly rate: AudioParam | null;
  readonly osc: OscillatorNode | null;
  readonly band: BiquadFilterNode | null;
  /** Who this voice follows (an enemy record), or null. */
  owner: object | null;
  on: boolean;
  /** Next position is a jump, not a glide (a newly assigned voice). */
  fresh: boolean;
  doppler: number;
}

/** Peak level of each kind at the reference distance. */
const LOOP_LEVEL: Record<LoopKind, number> = { heli: 0.45, ship: 0.35, jet: 0.7 };
/** An enemy jet's whine and the centre of its roar before Doppler. */
const JET_WHINE = 240;
const JET_BAND = 1100;

export function createLoopVoice(
  ctx: BaseAudioContext,
  noise: AudioBuffer,
  dest: AudioNode,
  kind: LoopKind,
  i: number,
): LoopVoice {
  const pan = panner(ctx, dest);
  const level = silentGain(ctx, pan);
  let rate: AudioParam | null = null;
  let o: OscillatorNode | null = null;
  let band: BiquadFilterNode | null = null;
  if (kind === 'heli') {
    // Rotor chop: band-passed noise and a low thump, chopped by a falling ramp at the blade rate.
    const src = noiseSource(ctx, noise, 0.5 + i * 0.61);
    const bp = bandpass(ctx, 'bandpass', 260, 1.1);
    const chop = silentGain(ctx, level, 0.5);
    src.connect(bp).connect(chop);
    const thump = osc(ctx, 'sine', 62 + i * 3);
    const thumpGain = silentGain(ctx, chop, 0.6);
    thump.connect(thumpGain);
    const lfo = osc(ctx, 'sawtooth', 14.5 + i * 1.3);
    lfo.connect(modulate(ctx, chop.gain, -0.5));
  } else if (kind === 'ship') {
    // Diesel: a low saw through a resonant low-pass, chugging at about 7 Hz.
    const saw = osc(ctx, 'sawtooth', 44 + i * 5);
    const lp = bandpass(ctx, 'lowpass', 240, 2);
    const chug = silentGain(ctx, level, 0.6);
    saw.connect(lp).connect(chug);
    const src = noiseSource(ctx, noise, 1.1 + i * 0.29);
    const wash = bandpass(ctx, 'lowpass', 320, 0.7);
    const washGain = silentGain(ctx, chug, 0.35);
    src.connect(wash).connect(washGain);
    const lfo = osc(ctx, 'sine', 7 + i * 0.8);
    lfo.connect(modulate(ctx, chug.gain, 0.35));
  } else {
    // Enemy jet: a roar of band-passed noise and a whine; Doppler moves all three.
    const src = noiseSource(ctx, noise, 0.9 + i * 0.43);
    rate = src.playbackRate;
    band = bandpass(ctx, 'bandpass', JET_BAND, 0.6);
    src.connect(band).connect(level);
    o = osc(ctx, 'sawtooth', JET_WHINE);
    const lp = bandpass(ctx, 'lowpass', 1600, 0.7);
    const whine = silentGain(ctx, level, 0.25);
    o.connect(lp).connect(whine);
  }
  return { kind, level, pan, rate, osc: o, band, owner: null, on: false, fresh: true, doppler: 1 };
}

function position(p: PannerNode, now: number, x: number, y: number, z: number, jump: boolean): void {
  if (jump) {
    setAt(p.positionX, now, now, x);
    setAt(p.positionY, now, now, y);
    setAt(p.positionZ, now, now, z);
  } else {
    glide(p.positionX, now, x, 0.04);
    glide(p.positionY, now, y, 0.04);
    glide(p.positionZ, now, z, 0.04);
  }
}

/** Follows a source at (rx, ry, rz) from the jet with Doppler factor `d`; `owner` null fades the voice out. */
export function setLoop(
  v: LoopVoice,
  now: number,
  owner: object | null,
  rx: number,
  ry: number,
  rz: number,
  d: number,
): void {
  if (owner !== v.owner) {
    v.owner = owner;
    v.fresh = true;
  }
  const on = owner !== null;
  if (on !== v.on) {
    v.on = on;
    glide(v.level.gain, now, on ? LOOP_LEVEL[v.kind] : 0, on ? 0.08 : 0.2);
  }
  if (!on) return;
  position(v.pan, now, rx, ry, rz, v.fresh);
  if (v.rate && v.osc && v.band && (v.fresh || Math.abs(d - v.doppler) > 0.002)) {
    v.doppler = d;
    glide(v.rate, now, d, 0.05);
    glide(v.osc.frequency, now, JET_WHINE * d, 0.05);
    glide(v.band.frequency, now, JET_BAND * d, 0.05);
  }
  v.fresh = false;
}

// ---------------------------------------------------------------------------------------------------------------
// Signals

export interface Signals {
  readonly refuelOsc: OscillatorNode;
  readonly refuelGain: GainNode;
  readonly alarmOsc: OscillatorNode;
  readonly alarmGain: GainNode;
  /** Audio time of the next blip or alarm figure; 0 while silent. */
  nextRefuel: number;
  nextAlarm: number;
  blips: number;
  figures: number;
}

export function createSignals(ctx: BaseAudioContext, dest: AudioNode): Signals {
  const refuelOsc = osc(ctx, 'sine', 440);
  const refuelGain = silentGain(ctx, dest);
  refuelOsc.connect(refuelGain);
  const alarmOsc = osc(ctx, 'square', 988);
  const band = bandpass(ctx, 'bandpass', 1500, 2.5);
  const alarmGain = silentGain(ctx, dest);
  alarmOsc.connect(band).connect(alarmGain);
  return { refuelOsc, refuelGain, alarmOsc, alarmGain, nextRefuel: 0, nextAlarm: 0, blips: 0, figures: 0 };
}

/** Look-ahead for scheduling blips and alarm figures (s). */
const AHEAD = 0.1;

/**
 * The refuel tone while over a barge, and the low-fuel alarm (judgment call 51): a falling B5 → F♯5 figure that
 * repeats faster as the tank empties.
 */
export function setSignals(s: Signals, now: number, refuel: boolean, alarm: boolean, fuel: number): void {
  if (refuel) {
    if (s.nextRefuel < now) s.nextRefuel = now + 0.01;
    while (s.nextRefuel < now + AHEAD) {
      const t = s.nextRefuel;
      s.refuelOsc.frequency.setValueAtTime(refuelHz(fuel) * (s.blips % 2 ? 1.5 : 1), t);
      note(s.refuelGain.gain, t, 0.15, 0.006, 0.025);
      s.blips++;
      s.nextRefuel += REFUEL_PERIOD;
    }
  } else if (s.nextRefuel) {
    s.nextRefuel = 0;
    s.refuelGain.gain.cancelScheduledValues(now);
    s.refuelGain.gain.setTargetAtTime(0, now, 0.01);
  }
  if (alarm) {
    if (s.nextAlarm < now) s.nextAlarm = now + 0.01;
    while (s.nextAlarm < now + AHEAD) {
      const t = s.nextAlarm;
      const f = s.alarmOsc.frequency;
      const g = s.alarmGain.gain;
      f.setValueAtTime(hz(83), t);
      g.setTargetAtTime(0, Math.max(0, t - 0.006), 0.0015);
      g.setValueAtTime(0, t);
      g.linearRampToValueAtTime(0.3, t + 0.01);
      g.setValueAtTime(0.3, t + 0.08);
      g.linearRampToValueAtTime(0, t + 0.1);
      f.setValueAtTime(hz(78), t + 0.11);
      g.linearRampToValueAtTime(0.27, t + 0.12);
      g.setValueAtTime(0.27, t + 0.22);
      g.linearRampToValueAtTime(0, t + 0.25);
      s.figures++;
      s.nextAlarm += Math.max(alarmPeriod(fuel), 0.28);
    }
  } else if (s.nextAlarm) {
    s.nextAlarm = 0;
    s.alarmGain.gain.cancelScheduledValues(now);
    s.alarmGain.gain.setTargetAtTime(0, now, 0.01);
  }
}
